import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { forwardRef } from 'react';
import { Amount, CancellationPolicyTag } from '@mews-distributor/common';
import { CancellationFee } from '@mews-distributor/data-access';
import { Banner, Body, Icon, IconName, Stack, StackItem, Tooltip, WithChildren } from '@optimus-web/core';
import { useAppSelector } from '../../../utils/redux';
import { areFlexibleRatesEnabled } from '../../configuration/selectors';
import { getEnterpriseTimezone } from '../../entities/enterprises/selectors';
import { getServiceEnterpriseId } from '../../entities/services/selectors';
import { CancellationPolicySimpleTag } from './CancellationPolicySimpleTag';
import { BannerText } from './RateCancellationBannerText';

interface RateCancellationPolicyBannerProps {
    cancellationFees: CancellationFee[];
    serviceId?: string;
    totalAmount: Amount;
    cancellationPolicyTag?: CancellationPolicyTag;
}

const timezoneToReadable = (timezone: string) => timezone.replace(/_/g, ' ').replace(/\//g, ' - ');

const SmallHelpIcon = forwardRef<HTMLSpanElement, WithChildren>(({ children, ...props }, ref) => (
    <Icon {...props} ref={ref} value={IconName.Help} variant="basic" size="small" />
));

const TimezoneTooltip = Tooltip(SmallHelpIcon);

export const RateCancellationPolicyBanner = ({
    serviceId,
    cancellationFees,
    totalAmount,
    cancellationPolicyTag,
}: RateCancellationPolicyBannerProps) => {
    const flexibleRatesEnabled = useAppSelector(areFlexibleRatesEnabled);

    const timezone = useAppSelector((state) => {
        if (serviceId == null) {
            return null;
        }

        const enterpriseId = getServiceEnterpriseId(state, { serviceId });

        return enterpriseId != null ? getEnterpriseTimezone(state, { enterpriseId }) : null;
    });

    if (!flexibleRatesEnabled || serviceId == null || timezone == null) {
        return null;
    }

    return (
        <Stack vertical spacing="200">
            {cancellationPolicyTag != null ? (
                <StackItem>
                    <CancellationPolicySimpleTag cancellationPolicyTag={cancellationPolicyTag} />
                </StackItem>
            ) : null}
            {array.size(cancellationFees) !== 0 ? (
                <Banner
                    variant="info"
                    primaryText
                    secondaryText={
                        <Stack spacing="100" vertical>
                            <Stack spacing="50" vertical>
                                {pipe(
                                    cancellationFees,
                                    array.map((fee) => (
                                        <Stack spacing="100" key={fee.endUtc}>
                                            <Body size="medium">
                                                <BannerText
                                                    totalAmount={totalAmount}
                                                    cancellationFee={fee}
                                                    serviceId={serviceId}
                                                    timezone={timezone}
                                                />
                                            </Body>
                                            <TimezoneTooltip tooltip={timezoneToReadable(timezone)} />
                                        </Stack>
                                    ))
                                )}
                            </Stack>
                        </Stack>
                    }
                />
            ) : null}
        </Stack>
    );
};
